import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/buttons/Button';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import type { Quantity } from '~/graphql/graphql';

type BundleQuantityEditorProps = {
  quantity: Quantity;
  platformProductsLength: ComponentProps<
    typeof BundleQuantityInput
  >['platformProductsLength'];
  isSaving: boolean;
  piecesPerBundle?: number;
  isPiecesPerBundleClearable?: boolean;
  quantityToSave?: (
    quantity: Quantity,
    piecesPerBundle: number | undefined
  ) => Quantity;
  onSave: (quantity: Quantity, piecesPerBundle: number | undefined) => void;
  onCancel: () => void;
  testId: string;
};

export function BundleQuantityEditor({
  quantity: savedQuantity,
  platformProductsLength,
  isSaving,
  piecesPerBundle: savedPiecesPerBundle,
  isPiecesPerBundleClearable = false,
  quantityToSave = (quantity) => quantity,
  onSave,
  onCancel,
  testId,
}: Readonly<BundleQuantityEditorProps>) {
  const { t } = useTranslation('inventory');
  const [quantity, setQuantity] = useState<Quantity>({
    value: savedQuantity.value,
    unit: savedQuantity.unit,
  });
  const [piecesPerBundle, setPiecesPerBundle] = useState(savedPiecesPerBundle);
  const isChanged =
    quantity.value !== savedQuantity.value ||
    quantity.unit !== savedQuantity.unit ||
    piecesPerBundle !== savedPiecesPerBundle;
  const sendQuantity = quantityToSave(quantity, piecesPerBundle);

  const changePiecesPerBundle = (
    value: number | undefined,
    quantityKeepingBundleCount: Quantity
  ) => {
    setQuantity(quantityKeepingBundleCount);
    setPiecesPerBundle(value);
  };

  return (
    <div
      className="flex w-60 flex-col gap-2 text-left"
      data-testid={`${testId}-quantity-editor`}
    >
      <BundleQuantityInput
        quantity={quantity}
        platformProductsLength={platformProductsLength}
        onBundleChange={setQuantity}
        piecesPerBundle={piecesPerBundle}
        onPiecesPerBundleChange={changePiecesPerBundle}
        onPiecesPerBundleClear={
          isPiecesPerBundleClearable
            ? (quantityKeepingBundleCount) =>
                changePiecesPerBundle(undefined, quantityKeepingBundleCount)
            : undefined
        }
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          width="auto"
          variant="secondary"
          onClick={onCancel}
          data-testid={`${testId}-quantity-cancel`}
        >
          {t('inventory.cancel')}
        </Button>
        <Button
          size="sm"
          width="auto"
          variant="primary"
          onClick={() => onSave(sendQuantity, piecesPerBundle)}
          disabled={!isChanged || sendQuantity.value <= 0 || isSaving}
          data-testid={`${testId}-quantity-save`}
        >
          {t('inventory.buy_list.save_quantity')}
        </Button>
      </div>
    </div>
  );
}
